import { useEffect, useState } from "react";
import { SrIcon } from "@/components/ui/SrIcon";
import { fetchVoucherDocumentBlob } from "@/hooks/use-voucher-actions";
import { PdfCanvasViewer } from "./PdfCanvasViewer";

interface Props {
  voucherId: string;
  title: string;
}

/**
 * VIB-1033: dokument-preview for fordelings-editoren. Henter bilag-dokumentet
 * på mount via `fetchVoucherDocumentBlob` med format "all", så PowerOffice
 * returnerer hva som finnes uavhengig av de (upålitelige) has_pdf/has_images-
 * flaggene — mange backfilte bilag har dokument i PO selv om flagget er false.
 *
 * Innholdet valideres (tom blob / ugyldig PDF → «ingen dokument») så vi aldri
 * mater Chromes PDF-viewer en ugyldig blob den feiler på. Blob-URL revokes i
 * cleanup.
 */
export function VoucherDocumentPreview({ voucherId, title }: Props) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [contentType, setContentType] = useState("application/pdf");
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  // VIB-1033: zoom i side-ved-side-previewen — vanskelig å lese ellers.
  const [zoom, setZoom] = useState(100);

  useEffect(() => {
    let active = true;
    let createdUrl: string | null = null;
    setLoading(true);
    setMissing(false);
    setBlobUrl(null);
    setBlob(null);
    setZoom(100);

    (async () => {
      try {
        const { blobUrl: url, contentType: ct, blob } =
          await fetchVoucherDocumentBlob(voucherId, "all");
        createdUrl = url;

        // Valider at vi faktisk fikk et brukbart dokument. PO kan svare 200 med
        // tom/ugyldig blob for bilag uten vedlegg (journal-posteringer).
        const isPdf = ct.includes("pdf");
        const header = isPdf ? await blob.slice(0, 5).text() : "";
        const valid = blob.size > 0 && (!isPdf || header.startsWith("%PDF"));

        if (!active) {
          URL.revokeObjectURL(url);
          return;
        }
        if (!valid) {
          URL.revokeObjectURL(url);
          createdUrl = null;
          setMissing(true);
          setLoading(false);
          return;
        }
        setBlobUrl(url);
        setBlob(blob);
        setContentType(ct);
        setLoading(false);
      } catch {
        // 404/500 fra get-voucher-document = ingen dokument i PO.
        if (!active) return;
        setMissing(true);
        setLoading(false);
      }
    })();

    return () => {
      active = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [voucherId]);

  return (
    <div
      className="flex h-[70vh] flex-col rounded-md border bg-muted/20"
      data-testid="voucher-document-preview"
    >
      {blobUrl && (
        <div className="flex items-center justify-end gap-1 border-b px-2 py-1">
          <button
            type="button"
            onClick={() => setZoom((z) => Math.max(50, z - 25))}
            className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-muted text-muted-foreground"
            title="Zoom ut"
            aria-label="Zoom ut"
          >
            <SrIcon name="zoom-out" className="h-4 w-4" />
          </button>
          <span className="w-11 text-center text-xs tabular-nums text-muted-foreground">
            {zoom}%
          </span>
          <button
            type="button"
            onClick={() => setZoom((z) => Math.min(300, z + 25))}
            className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-muted text-muted-foreground"
            title="Zoom inn"
            aria-label="Zoom inn"
          >
            <SrIcon name="zoom-in" className="h-4 w-4" />
          </button>
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-auto">
        {loading && (
          <div className="flex h-full items-center justify-center gap-2 text-muted-foreground">
            <SrIcon name="loader2" className="w-5 h-5 animate-spin" />
            <span className="text-sm">Henter dokument fra PowerOffice...</span>
          </div>
        )}
        {!loading && missing && (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
            <SrIcon name="file" className="w-8 h-8 text-muted-foreground/50" />
            <p className="max-w-sm text-sm text-muted-foreground">
              Ingen dokument tilgjengelig for dette bilaget. Fordel basert på
              posten og kontoen.
            </p>
          </div>
        )}
        {blobUrl && blob &&
          (contentType.startsWith("image/") ? (
            // IKKE flex-container her: flex-shrink krymper bildet tilbake til
            // container-bredden og overstyrer width:zoom%. Bruk block + maxWidth
            // none så bildet faktisk vokser og containeren scroller.
            <div className="p-4">
              <img
                src={blobUrl}
                alt={title}
                style={{ width: `${zoom}%`, maxWidth: "none" }}
                className="block"
              />
            </div>
          ) : (
            // PDF: rendres med pdf.js (PdfCanvasViewer) så zoom faktisk forstørrer
            // skarpt. Chromes innebygde iframe-PDF-viewer kan ikke styres.
            <PdfCanvasViewer blobUrl={blobUrl} blob={blob} zoom={zoom} />
          ))}
      </div>
    </div>
  );
}
